import React, { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Heading, Text, Button, useToast, VStack, Spinner } from "@chakra-ui/react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";
import electricPlugAnimation from "../assets/lottie/electric-plug.json";
import deliveryAnimation from "../assets/lottie/delivery.json";

const Authorization = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { currentUser, getToken } = useAuth();
  const [authorizationError, setAuthorizationError] = useState(false);
  const [isAuthorizationInProgress, setIsAuthorizationInProgress] = useState(false);
  const [processed, setProcessed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const authorizationRef = useRef<Promise<void> | null>(null);

  const handleAuthorization = useCallback(async () => {
    if (authorizationRef.current) return;

    authorizationRef.current = (async () => {
      setIsLoading(true);
      setProcessed(true);
      if (isAuthorizationInProgress) return;

      setIsAuthorizationInProgress(true);
      try {
        const params = new URLSearchParams(location.search);
        const spapiOauthCode = params.get("spapi_oauth_code");
        const state = params.get("state");
        const sellingPartnerId = params.get("selling_partner_id");

        if (!spapiOauthCode || !state) {
          throw new Error("Missing required parameters");
        }

        // Cas : Login réussi (LWA) mais pas encore Authorize (SP-API)
        if (!sellingPartnerId) {
            toast({
                title: "Connexion réussie",
                description: "Redirection vers la page d'autorisation Amazon...",
                status: "info",
                duration: 3000,
            });
            setTimeout(() => {
                window.location.href = "https://sellercentral.amazon.fr/apps/authorize/consent?application_id=amzn1.sp.solution.101ee5a0-60df-4737-8cdd-2017bcc0c25d&version=beta";
            }, 1500);
            return;
        }

        const token = await getToken();

        const response = await fetch("/.netlify/functions/authorizeAmazon", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            spapi_oauth_code: spapiOauthCode,
            state: state,
            selling_partner_id: sellingPartnerId,
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to authorize Amazon account");
        }

        toast({
          title: t("authorizeSuccess"),
          status: "success",
          duration: 3000,
          isClosable: true,
        });

      } catch (error) {
        console.error("Error authorizing Amazon account:", error);
        setAuthorizationError(true);
      } finally {
        setIsAuthorizationInProgress(false);
        setProcessed(true);
        setIsLoading(false);
      }
    })();

    await authorizationRef.current;
    authorizationRef.current = null;
  }, [location, getToken, t, toast]);

  useEffect(() => {
    if (currentUser && !isLoading && !processed) {
      handleAuthorization();
    }
  }, [currentUser, isLoading, processed, handleAuthorization]);

  if(isLoading) {
    return  <Spinner size="sm" /> ;
  }
  return (
    <Box minHeight="100vh">
      <Box maxWidth="6xl" mx="auto" py={12} px={4}>
        <VStack spacing={8} align="center" textAlign="center">
         
          {authorizationError ? (
            <>
             <Box width="100px">
                <Lottie animationData={deliveryAnimation} loop={true} />
              </Box>
              <Heading as="h1" size="2xl" mb={4}>
                {t("authorizeError")}
              </Heading>
              <Text fontSize="xl" mb={8}>
                {t("authorizeErrorDescription")}
              </Text>
              <Button
                colorScheme="blue"
                size="lg"
                onClick={() =>
                  window.location.href =
                    "https://sellercentral.amazon.fr/apps/authorize/consent?application_id=amzn1.sp.solution.101ee5a0-60df-4737-8cdd-2017bcc0c25d"
                }
              >
                {t("retryAuthorization")}
              </Button>
            </>
          ) : (
            <>
             <Box width="100px">
                <Lottie animationData={electricPlugAnimation} loop={true} />
              </Box>
              <Heading as="h1" size="2xl" mb={4}>
                {t("authorizeSuccess")}
              </Heading>
              <Text fontSize="xl" mb={8}>
                {t("authorizeSuccessDescription")}
              </Text>
              <Button colorScheme="blue" size="lg" onClick={() => navigate("/account?tab=1")}>
                {t("my_account")}
              </Button>
            </>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default Authorization;
